import React from 'react';
import { PARTNER_OFFER_UPDATED_AT } from '../../config/legal';

/**
 * Публичная оферта для партнёров (партнёрский договор-оферта).
 *
 * Текст обязан **дословно совпадать** с `assets/legal/partner_terms.md`
 * мобильного приложения: версию оферты из этого файла приложение отправляет
 * на сервер при акцепте (`offer.version`), и именно она хранится как
 * доказательство того, что принял партнёр. Расходиться тексту и версии
 * между сайтом и приложением нельзя.
 *
 * Дата редакции берётся из `config/legal`: там же лежит версия, которая
 * уходит на сервер при акцепте. Двумя литералами их держать нельзя —
 * разъехавшись, они дадут акцепт редакции, которой человек не видел.
 */
export const PartnerTermsContent: React.FC = () => (
  <>
    <h2>Партнёрский договор-оферта на оказание информационно-технических и рекламных услуг для бизнеса в приложении «Qprice»</h2>
    <p>
      г. Астана. Редакция от {PARTNER_OFFER_UPDATED_AT}.
    </p>
    <p>
      Товарищество с ограниченной ответственностью «Big Brain Group» (БИН 211140024452),
      являющееся владельцем и оператором платформы «Qprice» (мобильное приложение и веб-сайт,
      далее — «Платформа», «Исполнитель»), публикует настоящую публичную оферту в соответствии
      со статьёй 395 Гражданского кодекса Республики Казахстан, адресованную неограниченному
      кругу юридических лиц, индивидуальных предпринимателей и самозанятых лиц, осуществляющих
      предпринимательскую деятельность (далее — «Партнёр», «Заказчик»), о нижеследующем.
    </p>

    <h2>1. ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ</h2>
    <p>
      1.1. «Платформа» / «Qprice» — мобильное приложение и веб-сайт, принадлежащие ТОО «Big
      Brain Group», предоставляющие Пользователям информацию об актуальных скидках, акциях и
      предложениях Партнёров.
    </p>
    <p>
      1.2. «Партнёр» — юридическое лицо, индивидуальный предприниматель или самозанятое лицо,
      осуществляющее деятельность в сфере услуг, торговли, общественного питания, развлечений
      или иную предпринимательскую деятельность, присоединившееся к настоящей Оферте в порядке
      раздела 3.
    </p>
    <p>
      1.3. «Пользователь» — физическое лицо, использующее мобильное приложение Qprice для
      поиска актуальных скидок и предложений Партнёров.
    </p>
    <p>
      1.4. «Личный кабинет Партнёра» — раздел Платформы, предоставляющий Партнёру инструменты
      для управления своим профилем, предложениями, скидками и подпиской.
    </p>
    <p>
      1.5. «Акцепт» — полное и безоговорочное принятие Партнёром условий настоящей Оферты
      путём совершения действий, указанных в разделе 3.
    </p>
    <p>
      1.6. «Абонентская плата» — периодический платёж, уплачиваемый Партнёром за доступ к
      функционалу Платформы согласно выбранному тарифному плану.
    </p>
    <p>
      1.7. «Flash-скидка» — динамическое скидочное предложение Партнёра с ограниченным сроком
      действия («горящие часы»), формируемое Партнёром самостоятельно в Личном кабинете.
    </p>
    <p>
      1.8. «Корзина» / «Заявка» — функция Приложения, позволяющая Пользователю сформировать
      перечень интересующих его товаров/услуг Партнёра и направить сформированную информацию
      Партнёру в порядке раздела 5 настоящей Оферты.
    </p>

    <h2>2. ПРЕДМЕТ ОФЕРТЫ</h2>
    <p>
      2.1. Платформа предоставляет Партнёру неисключительное право использования функционала
      мобильного приложения и Личного кабинета Партнёра, включая: размещение карточки
      заведения, отображение геопозиции, публикацию статических и динамических (Flash-скидки,
      «горящие часы») скидочных предложений для Пользователей, а также приём Заявок из Корзины
      Пользователей в порядке раздела 5.
    </p>
    <p>
      2.2. Платформа не является стороной сделок между Партнёром и Пользователем, не продаёт и
      не оказывает товары/услуги Партнёра, а выполняет исключительно
      информационно-технологическую функцию.
    </p>
    <p>
      2.3. Конкретный перечень функций, доступных Партнёру, определяется выбранным тарифным
      планом согласно Приложению № 1 к настоящей Оферте либо условиями программы «100
      Партнёров-Основателей» согласно разделу 4.
    </p>

    <h2>3. ПОРЯДОК АКЦЕПТА ОФЕРТЫ И РЕГИСТРАЦИЯ</h2>
    <p>
      3.1. Акцепт настоящей Оферты осуществляется Партнёром путём прохождения регистрации в
      приложении / веб-кабинете Платформы, указания данных, предусмотренных Приложением № 2 к
      настоящей Оферте, верификации номера телефона посредством SMS/OTP-кода, ознакомления с
      полным текстом настоящей Оферты и проставления отметки («галочки») в поле «Я
      ознакомлен(а) и согласен(на) с условиями Публичной оферты».
    </p>
    <p>
      3.2. С момента совершения действий, указанных в п. 3.1, договор между Партнёром и
      Платформой считается заключённым на условиях настоящей Оферты и имеет полную юридическую
      силу, равную силе двустороннего письменного договора, подписанного Сторонами (ст. 389,
      396 Гражданского кодекса Республики Казахстан).
    </p>
    <p>
      3.3. Платформа фиксирует и сохраняет дату, время акцепта, а также версию текста Оферты,
      действовавшую на момент акцепта, для целей подтверждения факта и содержания заключённого
      договора.
    </p>
    <p>
      3.4. Лицо, осуществляющее регистрацию от имени Партнёра — юридического лица,
      подтверждает, что обладает необходимыми полномочиями действовать от имени такого
      юридического лица. Партнёр несёт риск последствий отсутствия таких полномочий у лица,
      совершившего акцепт от его имени.
    </p>
    <p>
      3.5. Настоящая Оферта не заменяет собой отдельный двусторонний договор в случаях, когда
      Стороны согласовывают индивидуальные условия сотрудничества (крупные сети, эксклюзивные
      условия). В таких случаях положения настоящей Оферты применяются субсидиарно, в части,
      не урегулированной таким договором.
    </p>

    <h2>4. СПЕЦИАЛЬНЫЕ УСЛОВИЯ ПРОГРАММЫ «100 ПАРТНЁРОВ-ОСНОВАТЕЛЕЙ»</h2>
    <p>
      4.1. Первым 100 (ста) Партнёрам, акцептовавшим настоящую Оферту в рамках программы «100
      Партнёров-Основателей» (Founding Partners), предоставляется льготный период пользования
      Платформой продолжительностью 90 (девяносто) календарных дней со дня Акцепта со 100%
      скидкой (0 тенге).
    </p>
    <p>
      4.2. Предоставление льготного периода обусловлено принятием Партнёром на себя следующих
      встречных обязательств:
    </p>
    <ul>
      <li>
        размещение фирменных POS-материалов Платформы (NFC-метки, тейбл-тенты, QR-стойки) в
        кассовой зоне и/или на столах заведения Партнёра;
      </li>
      <li>
        поддержание актуальности информации в профиле и публикация активных акций/скидок не
        реже 2–3 (двух-трёх) раз в неделю;
      </li>
      <li>
        фактическое предоставление заявленной скидки Пользователям приложения при предъявлении
        Пользователем QR-кода и/или экрана приложения с активной скидкой.
      </li>
    </ul>
    <p>
      4.3. При невыполнении Партнёром обязательств, указанных в п. 4.2, Платформа вправе
      приостановить доступ Партнёра к льготным условиям до окончания срока льготного периода,
      переведя Партнёра на условия оплаты согласно Приложению № 1.
    </p>
    <p>
      4.4. Не позднее чем за 10–14 (десять-четырнадцать) календарных дней до окончания
      льготного периода Платформа направляет Партнёру автоматическое уведомление об окончании
      льготного периода с приложением статистики/отчёта об эффективности размещения Партнёра
      (просмотры, клики, переходы) за истекший период.
    </p>
    <p>
      4.5. По истечении льготного периода дальнейшее использование Платформы Партнёром
      осуществляется на условиях раздела 6 настоящей Оферты и по тарифам, указанным в
      Приложении № 1, если Партнёр не выразит отказ от дальнейшего использования Платформы в
      порядке раздела 9.
    </p>
    <p>
      4.6. Платформа вправе досрочно завершить действие программы «100 Партнёров-Основателей»
      по достижении лимита в 100 (сто) участников, о чём информация публикуется в
      приложении/на сайте Платформы; ранее подключившиеся по программе Партнёры сохраняют
      условия льготного периода в полном объёме до его истечения.
    </p>

    <h2>5. ФУНКЦИЯ БРОНИРОВАНИЯ (КОРЗИНА) И ПЕРЕДАЧА ЗАЯВОК ПАРТНЁРУ</h2>
    <p>
      5.1. Платформа предоставляет Пользователям функцию формирования Заявки (Корзины) с
      перечнем интересующих их товаров/услуг Партнёра. При подтверждении Заявки Пользователем
      сформированная информация направляется Партнёру посредством мессенджера WhatsApp на
      номер, указанный Партнёром при регистрации согласно Приложению № 2 к настоящей Оферте.
    </p>
    <p>
      5.2. Платформа не осуществляет приём, обработку или перевод каких-либо денежных средств
      в связи с Заявками из Корзины. Функция Корзины ограничивается исключительно технической
      передачей сформированной информации Партнёру и не является бронированием,
      резервированием или заключением сделки между Пользователем и Партнёром.
    </p>
    <p>
      5.3. Платформа не гарантирует и не контролирует получение, прочтение либо какую-либо
      обработку Партнёром направленной ему Заявки. Факт отправки Заявки Пользователем не
      создаёт для Партнёра каких-либо обязательств перед Пользователем и не влечёт
      ответственности Платформы за дальнейшие действия или бездействие Партнёра.
    </p>
    <p>
      5.4. Партнёр обязан самостоятельно отслеживать поступающие через WhatsApp Заявки и
      своевременно связываться с Пользователями для подтверждения, уточнения деталей и
      дальнейшего оформления брони, заказа или оказания услуги напрямую, без участия
      Платформы.
    </p>
    <p>
      5.5. Партнёр обязан обеспечивать своевременный мониторинг указанного номера WhatsApp и
      вправе в любой момент изменить номер для приёма Заявок через Личный кабинет.
    </p>
    <p>
      5.6. Платформа отображает Пользователю уведомление о том, что Заявка направлена Партнёру
      и не является подтверждённой бронью, с рекомендацией дождаться ответа Партнёра либо
      связаться с ним напрямую для подтверждения.
    </p>

    <h2>6. ПРАВА И ОБЯЗАННОСТИ ПЛАТФОРМЫ</h2>
    <p>
      6.1. Платформа обязуется:
    </p>
    <ul>
      <li>
        обеспечить техническую возможность размещения информации Партнёра в мобильном
        приложении и её отображение Пользователям;
      </li>
      <li>
        предоставить Партнёру доступ к Личному кабинету для самостоятельного управления
        скидками, расписанием их действия, содержанием профиля и номером WhatsApp для приёма
        Заявок;
      </li>
      <li>
        уведомлять Партнёра об изменении условий настоящей Оферты и тарифов не менее чем за 10
        (десять) календарных дней до вступления изменений в силу.
      </li>
    </ul>
    <p>
      6.2. Платформа вправе:
    </p>
    <ul>
      <li>
        приостановить отображение профиля Партнёра в приложении в случае просрочки Абонентской
        платы либо невыполнения условий программы «100 Партнёров-Основателей», до устранения
        соответствующего нарушения;
      </li>
      <li>
        отказать в размещении или удалить информацию Партнёра, которая содержит недостоверные
        сведения, вводит Пользователей в заблуждение либо нарушает законодательство Республики
        Казахстан;
      </li>
      <li>
        в одностороннем порядке заблокировать доступ Партнёра к Платформе при получении
        обоснованных жалоб Пользователей на недобросовестное поведение Партнёра (в том числе
        на непредоставление заявленной скидки либо систематическое игнорирование Заявок из
        Корзины), с уведомлением Партнёра о причинах блокировки;
      </li>
      <li>
        изменять функциональность, дизайн и технические характеристики Платформы без
        предварительного согласования с Партнёром, если это не ухудшает существенно условия
        оказания услуг по выбранному тарифу.
      </li>
    </ul>

    <h2>7. ПРАВА И ОБЯЗАННОСТИ ПАРТНЁРА</h2>
    <p>
      7.1. Партнёр обязуется:
    </p>
    <ul>
      <li>
        предоставлять достоверную, точную и актуальную информацию о своей деятельности,
        товарах, услугах, ценах и скидках, размещаемую в Личном кабинете;
      </li>
      <li>
        самостоятельно и своевременно поддерживать актуальность размещённых скидок и
        предложений;
      </li>
      <li>
        фактически предоставлять Пользователям заявленные скидки на условиях, указанных в
        профиле Партнёра на Платформе;
      </li>
      <li>
        своевременно уплачивать Абонентскую плату согласно выбранному тарифному плану либо
        соблюдать условия программы «100 Партнёров-Основателей»;
      </li>
      <li>
        своевременно обрабатывать Заявки, поступающие из Корзины через WhatsApp, и
        самостоятельно взаимодействовать с Пользователями по вопросам бронирования, оплаты и
        оказания услуг;
      </li>
      <li>
        незамедлительно информировать Платформу об изменении реквизитов, номера WhatsApp,
        режима работы, приостановлении или прекращении деятельности;
      </li>
      <li>
        нести самостоятельную ответственность за качество, безопасность и легальность товаров
        и услуг, предоставляемых Пользователям, соблюдение прав потребителей, налогового и
        иного применимого законодательства Республики Казахстан.
      </li>
    </ul>
    <p>
      7.2. Партнёр вправе самостоятельно определять размер и период действия предоставляемых
      скидок, а также расторгнуть договор в любое время в порядке раздела 9 настоящей Оферты.
    </p>
    <p>
      7.3. Партнёр гарантирует, что обладает всеми правами на используемые при регистрации
      материалы (наименование, логотип, фотографии, описание), и что их использование
      Платформой не нарушает прав третьих лиц.
    </p>

    <h2>8. СТОИМОСТЬ УСЛУГ, ТАРИФЫ И ПОРЯДОК РАСЧЁТОВ</h2>
    <p>
      8.1. По истечении льготного периода программы «100 Партнёров-Основателей» (для её
      участников) либо с момента Акцепта (для иных Партнёров) размер Абонентской платы
      определяется выбранным тарифным планом согласно Приложению № 1 к настоящей Оферте.
    </p>
    <p>
      8.2. Оплата производится Партнёром на условиях предоплаты за расчётный период через
      платёжный виджет банка-эквайера, интегрированный в Личный кабинет, либо, для отдельных
      Партнёров по согласованию с Платформой, — безналичным переводом на расчётный счёт
      Платформы по выставленному счёту. Каждой операции оплаты присваивается уникальный
      идентификатор заказа, связанный с учётной записью Партнёра в Личном кабинете, что
      позволяет Платформе однозначно определить, за какого Партнёра поступил платёж,
      независимо от того, с использованием какого платёжного средства фактически произведена
      оплата.
    </p>
    <p>
      8.3. Подтверждением факта оплаты является уведомление, полученное Платформой от
      банка-эквайера по защищённому серверному каналу. Платформа не обязана засчитывать оплату
      на основании одного лишь отображения в браузере Партнёра сообщения об успешной оплате,
      если соответствующее уведомление от банка-эквайера не поступило.
    </p>
    <p>
      8.4. При отсутствии оплаты по истечении оплаченного (либо льготного) периода профиль
      Партнёра автоматически скрывается из результатов поиска и не отображается Пользователям
      Платформы до момента поступления оплаты, без права Партнёра требовать возмещения
      упущенной выгоды за период приостановки.
    </p>
    <p>
      8.5. Платформа вправе изменять размер Абонентской платы и структуру тарифов в
      одностороннем порядке с уведомлением Партнёра в порядке п. 6.1 настоящей Оферты.
    </p>
    <p>
      8.6. Уплаченная Абонентская плата за прошедший период не подлежит возврату, за
      исключением случаев одностороннего прекращения оказания услуг по вине Платформы.
    </p>

    <h2>9. ОТВЕТСТВЕННОСТЬ СТОРОН И ОГРАНИЧЕНИЕ ОТВЕТСТВЕННОСТИ ПЛАТФОРМЫ</h2>
    <p>
      9.1. Платформа выступает исключительно информационной платформой и не является стороной
      сделки купли-продажи товаров и/или оказания услуг между Партнёром и Пользователем, в том
      числе в отношении Заявок, направленных через функцию Корзины.
    </p>
    <p>
      9.2. Партнёр несёт полную ответственность за качество, безопасность и правомерность
      применения скидок, а также за фактическое исполнение обязательств перед Пользователем,
      включая обязательства, возникшие в результате обработки Заявки, направленной через
      WhatsApp в порядке раздела 5.
    </p>
    <p>
      9.3. Платформа не несёт ответственности за неполучение, несвоевременное получение либо
      игнорирование Партнёром Заявок Пользователей, а также за любые последствия, возникшие
      вследствие технических сбоев мессенджера WhatsApp, находящегося вне контроля Платформы.
    </p>
    <p>
      9.4. Все претензии Пользователей, связанные с качеством товаров/услуг Партнёра,
      несоответствием заявленной и фактически предоставленной скидки, необработанной Заявкой
      либо иными аспектами взаимодействия Пользователя и Партнёра, подлежат разрешению
      непосредственно между Пользователем и Партнёром, без участия Платформы.
    </p>
    <p>
      9.5. Партнёр самостоятельно несёт полную ответственность перед Пользователями,
      Платформой и государственными органами за любое содержание размещаемой им информации
      (тексты, фотографии, логотипы, описания, скидки и иные материалы), включая её
      достоверность, соответствие законодательству Республики Казахстан о защите прав
      потребителей и о рекламе, а также отсутствие нарушения прав третьих лиц, включая
      интеллектуальную собственность, честь, достоинство и деловую репутацию.
    </p>
    <p>
      9.6. Партнёр обязуется возместить Платформе любые убытки, включая штрафы, судебные
      издержки и суммы, фактически выплаченные третьим лицам, возникшие в связи с претензиями
      третьих лиц к контенту, размещённому Партнёром на Платформе.
    </p>
    <p>
      9.7. Платформа предоставляет Пользователям функцию подачи жалобы на контент, размещённый
      Партнёром (в том числе на недостоверную, оскорбительную информацию либо информацию,
      нарушающую права третьих лиц). При получении такой жалобы Платформа вправе временно
      скрыть оспариваемый контент до выяснения обстоятельств, запросить у Партнёра пояснения и
      подтверждающие документы, а по результатам рассмотрения — удалить контент и/или
      ограничить либо заблокировать доступ Партнёра в порядке п. 6.2 настоящей Оферты.
    </p>
    <p>
      9.8. Совокупная ответственность Платформы перед Партнёром по любым основаниям, связанным
      с настоящей Офертой, ограничена суммой Абонентской платы, уплаченной Партнёром за
      последние 3 (три) месяца, предшествующие возникновению оснований для ответственности.
    </p>

    <h2>10. ИНТЕЛЛЕКТУАЛЬНАЯ СОБСТВЕННОСТЬ И ПЕРСОНАЛЬНЫЕ ДАННЫЕ</h2>
    <p>
      10.1. Партнёр предоставляет Платформе неисключительную, безвозмездную лицензию на
      использование предоставленных им материалов (наименование, логотип, фотографии,
      описание, меню/прайс) исключительно в целях отображения информации о Партнёре в
      приложении Qprice на весь срок действия настоящего договора.
    </p>
    <p>
      10.2. Обработка персональных данных представителя Партнёра, указанных при регистрации,
      осуществляется Платформой в соответствии с Законом Республики Казахстан «О персональных
      данных и их защите» и Политикой конфиденциальности Платформы, размещённой на сайте/в
      приложении.
    </p>
    <p>
      10.3. Партнёр даёт согласие на получение от Платформы информационных и рекламных
      сообщений, связанных с функционированием Платформы, с правом отказа от такой рассылки в
      любое время через настройки Личного кабинета.
    </p>
    <p>
      10.4. Номер WhatsApp Партнёра, предоставленный для приёма Заявок, используется
      Платформой исключительно для целей технической передачи Заявок Пользователей и не
      используется Платформой в иных целях без согласия Партнёра.
    </p>

    <h2>11. СРОК ДЕЙСТВИЯ, ИЗМЕНЕНИЕ И РАСТОРЖЕНИЕ</h2>
    <p>
      11.1. Договор вступает в силу с момента Акцепта и действует до его расторжения любой из
      Сторон.
    </p>
    <p>
      11.2. Партнёр вправе в любое время расторгнуть договор, уведомив Платформу через Личный
      кабинет либо иным доступным способом связи. Уплаченная Абонентская плата за
      неиспользованный период не возвращается, если иное прямо не предусмотрено настоящей
      Офертой.
    </p>
    <p>
      11.3. Платформа вправе внести изменения в настоящую Оферту в одностороннем порядке.
      Новая редакция Оферты вступает в силу с момента её публикации на Платформе, если иной
      срок не указан в тексте изменений. Продолжение использования Личного кабинета Партнёром
      после вступления изменений в силу считается согласием с новой редакцией.
    </p>
    <p>
      11.4. Платформа вправе в одностороннем порядке отказаться от исполнения договора с
      Партнёром, систематически нарушающим условия настоящей Оферты (в том числе условия
      программы «100 Партнёров-Основателей», либо многократно предоставляющим недостоверную
      информацию о скидках или игнорирующим Заявки Пользователей), с уведомлением Партнёра о
      причинах такого отказа.
    </p>

    <h2>12. РАЗРЕШЕНИЕ СПОРОВ И ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ</h2>
    <p>
      12.1. Настоящая Оферта регулируется и толкуется в соответствии с законодательством
      Республики Казахстан.
    </p>
    <p>
      12.2. Все споры и разногласия, возникающие из настоящей Оферты, разрешаются путём
      переговоров, а при недостижении согласия — в судебном порядке по месту нахождения
      Платформы.
    </p>
    <p>
      12.3. Если какое-либо положение настоящей Оферты будет признано недействительным, это не
      влечёт недействительности остальных положений.
    </p>
    <p>
      12.4. Неотъемлемой частью настоящей Оферты являются: Приложение № 1 — Тарифные планы;
      Приложение № 2 — Перечень данных, предоставляемых Партнёром при регистрации.
    </p>

    <h2>Реквизиты Платформы</h2>
    <p>
      ТОО «Big Brain Group», БИН 211140024452
    </p>

    <h2>Приложение № 1. ТАРИФНЫЕ ПЛАНЫ</h2>
    <p>
      к Публичной оферте (Партнёрскому договору-оферте платформы «Qprice»)
    </p>
    <ul>
      <li>
        1 месяц — 9 990 тг / месяц (предоплата);
      </li>
      <li>
        6 месяцев — 49 990 тг единоразово за период;
      </li>
      <li>
        12 месяцев — 94 990 тг единоразово за период.
      </li>
    </ul>
    <p>
      Первым 100 Партнёрам, подключившимся по программе «100 Партнёров-Основателей», первые 90
      календарных дней предоставляются бесплатно (0 тг) на условиях раздела 4 настоящей
      Оферты. Функционал по всем тарифам одинаков и включает полный доступ к Личному кабинету,
      размещению скидок, Flash-скидок и приёму Заявок из Корзины.
    </p>
    <p>
      Актуальные тарифы публикуются в Личном кабинете Партнёра и на сайте Платформы.
    </p>

    <h2>Приложение № 2. ПЕРЕЧЕНЬ ДАННЫХ, ПРЕДОСТАВЛЯЕМЫХ ПАРТНЁРОМ ПРИ РЕГИСТРАЦИИ</h2>
    <p>
      к Публичной оферте (Партнёрскому договору-оферте платформы «Qprice»)
    </p>

    <h3>1. Регистрационные (юридические) данные</h3>
    <ul>
      <li>
        Полное наименование юридического лица / ИП — обязательно;
      </li>
      <li>
        БИН/ИИН — обязательно;
      </li>
      <li>
        ФИО, должность и контакты лица, регистрирующего аккаунт — обязательно;
      </li>
      <li>
        Юридический и фактический адрес — обязательно;
      </li>
      <li>
        Контактный телефон — обязательно;
      </li>
      <li>
        E-mail — обязательно;
      </li>
      <li>
        Номер WhatsApp для получения заявок из Корзины — обязательно;
      </li>
      <li>
        Банковские реквизиты (БИК, ИИК) — опционально, только при оплате по счёту.
      </li>
    </ul>

    <h3>2. Данные для отображения в приложении (профиль Партнёра)</h3>
    <ul>
      <li>
        Название заведения/бизнеса (публичное) — обязательно;
      </li>
      <li>
        Категория/вид деятельности (кухня, тип услуг) — обязательно;
      </li>
      <li>
        Геолокация (адрес + координаты) — обязательно;
      </li>
      <li>
        Режим работы — обязательно;
      </li>
      <li>
        Логотип и фотографии заведения/товара — обязательно;
      </li>
      <li>
        Меню / прайс-лист / перечень услуг — обязательно;
      </li>
      <li>
        Действующие скидки: размер, период и условия действия — обязательно;
      </li>
      <li>
        Контакты для связи с Пользователем (телефон, WhatsApp) — обязательно;
      </li>
      <li>
        Ссылки на соцсети (Instagram и т.п.) — опционально.
      </li>
    </ul>

    <h3>3. Согласия и подтверждения</h3>
    <ul>
      <li>
        согласие на обработку персональных данных представителя Партнёра;
      </li>
      <li>
        подтверждение полномочий лица, совершающего Акцепт, действовать от имени Партнёра;
      </li>
      <li>
        подтверждение достоверности предоставленных сведений;
      </li>
      <li>
        подтверждение наличия прав на используемые материалы (лого, фото, название);
      </li>
      <li>
        акцепт настоящей Публичной оферты (галочка + фиксация даты/времени/версии текста).
      </li>
    </ul>
    <p>
      Примечание: банковские реквизиты у Партнёра по умолчанию не запрашиваются — оплата
      принимается через платёжный виджет банка-эквайера, привязка платежа к Партнёру
      осуществляется автоматически по идентификатору заказа в Личном кабинете (см. п. 8.2
      настоящей Оферты). Реквизиты запрашиваются только у Партнёров, выбравших оплату по
      счёту. Номер WhatsApp обязателен отдельно от общего контактного телефона, поскольку
      именно на него направляются Заявки Пользователей из Корзины.
    </p>
  </>
);
